import { Fragment, FunctionComponent, useCallback, useEffect, useState } from 'react';

import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import styled from 'styled-components';

import { HL7SendModel } from 'models/HL7SendModel';

import { apiClient } from 'core/api/globals';
import { DEFAULT_DATA_TABLE_DATA_STATE, DEFAULT_PAGE_SIZES, DataTable, HeaderCell, Page, PageHeader, TextCell } from 'core/ui';

// Define Props
interface HL7SendHomeProps {
  examId?: number;
}

export const HL7SendHome: FunctionComponent<HL7SendHomeProps> = ({ examId }) => {
  const [hl7SendData, setHL7SendData] = useState<HL7SendModel[]>([]);
  const [hl7SendTotal, setHL7SendTotal] = useState(0);
  const [dataState, setDataState] = useState<State>(DEFAULT_DATA_TABLE_DATA_STATE);

  useEffect(() => {
    (async () => {
      // Construct state with potential filter
      const stateToSend = { ...dataState };

      // --- Add examId filter if provided ---
      if (examId !== undefined && examId !== null) {
        const examIdFilter: FilterDescriptor = { field: 'examId', operator: 'eq', value: examId };
        const existingFilter = stateToSend.filter;

        if (existingFilter) {
          stateToSend.filter = {
            logic: 'and',
            filters: [existingFilter, examIdFilter],
          };
        } else {
          stateToSend.filter = {
            logic: 'and',
            filters: [examIdFilter],
          };
        }
      }
      // --- End examId filter ---

      // Use stateToSend in API call
      const queryResult = await apiClient.hl7SendClient.getAllSourcesForKendoGrid(stateToSend);

      setHL7SendData(queryResult.data);
      setHL7SendTotal(queryResult.total);
    })();
  }, [dataState, examId]);

  const PageableSettings = {
    pageSizes: DEFAULT_PAGE_SIZES,
  };

  const handleDataStateChange = useCallback((changeEvent: GridDataStateChangeEvent) => {
    setDataState(changeEvent.dataState);
  }, []);

  // Determine wrapper component
  const Wrapper = examId ? Fragment : Page;

  return (
    <Wrapper>
      {/* Conditionally render header */}
      {!examId && <PageHeader title="HL7Send" />}

      <StyledDataTable
        data={hl7SendData}
        filterable
        sortable
        pageable={PageableSettings}
        total={hl7SendTotal}
        onDataStateChange={handleDataStateChange}
        {...dataState}
      >
        <GridColumn title="Id" field="id" filter="numeric" headerCell={HeaderCell} cell={TextCell} width={80} />
        <GridColumn title="Control Id" field="controlId" filter="text" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="Integration" field="integraiton" filter="text" headerCell={HeaderCell} cell={TextCell} />
        <GridColumn title="Message" field="message" filter="text" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="Exam Id" field="examId" filter="numeric" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="Date Sent" field="dateSent" filter="date" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="Response" field="response" filter="text" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="Date Created" field="dateCreated" filter="date" headerCell={HeaderCell} cell={TextCell} width={100} />
        <GridColumn title="User Created" field="userCreated" filter="text" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="Date Modified" field="dateModified" filter="date" headerCell={HeaderCell} cell={TextCell} width={300} />
        <GridColumn title="User Modified" field="User Modified" filter="text" headerCell={HeaderCell} cell={TextCell} width={300} />
      </StyledDataTable>
    </Wrapper>
  );
};

HL7SendHome.displayName = 'HL7SendHome';

const StyledDataTable = styled(DataTable)`
  max-height: initial;
`;
